import React from 'react';
import { BookOpen, ChevronRight } from 'lucide-react';
import { navigateToJyotishaTopic } from '../../utils/jyotishaTopicNavigation';
import { ExpandableSection } from '../shared/ExpandableSection';

/**
 * Sleek related lesson links - collapsed by default, expand on demand.
 * @param {{
 *   lessons: { topicId: string; label: string; groupLabel?: string; section?: string }[];
 *   isDark: boolean;
 *   showEnglish: boolean;
 *   onPageChange?: (pageId: string) => void;
 * }} props
 */
export function BhriguRelatedLessonLinks({ lessons, isDark, showEnglish, onPageChange }) {
  if (!lessons?.length) return null;

  const muted = isDark ? 'text-gray-500' : 'text-gray-500';
  const chip = isDark
    ? 'border-white/10 bg-white/[0.05] text-gray-100 hover:border-violet-400/40 hover:bg-violet-500/10'
    : 'border-amber-200/90 bg-white text-gray-800 hover:border-violet-300 hover:bg-violet-50';

  const count = Math.min(lessons.length, 4);
  const summaryLabel = showEnglish ? 'Related lessons' : 'संबंधित पाठ';

  const openLesson = (topicId) => {
    if (onPageChange) navigateToJyotishaTopic(onPageChange, topicId);
  };

  return (
    <ExpandableSection
      className={`mb-1 rounded-lg border ${isDark ? 'border-white/10 bg-white/[0.03]' : 'border-amber-200/70 bg-white/50'}`}
      summaryClassName={`px-2.5 py-2 text-xs font-semibold ${isDark ? 'text-gray-200 hover:bg-white/[0.04]' : 'text-gray-800 hover:bg-amber-50/80'} ${showEnglish ? '' : 'font-devanagari'}`}
      contentClassName="space-y-1 border-t border-inherit px-2 py-2"
      chevronClassName={muted}
      summary={
        <span className="flex min-w-0 items-center gap-1.5" lang={showEnglish ? 'en' : 'hi'}>
          <BookOpen className={`h-3.5 w-3.5 shrink-0 ${muted}`} aria-hidden />
          <span className="truncate">{summaryLabel}</span>
          <span
            className={`inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-bold ${
              isDark ? 'bg-violet-500/20 text-violet-200' : 'bg-violet-100 text-violet-800'
            }`}
          >
            {count}
          </span>
        </span>
      }
    >
      {lessons.slice(0, 4).map((l) => (
        <button
          key={`${l.section || 'jyotisha'}-${l.topicId}`}
          type="button"
          onClick={() => openLesson(l.topicId)}
          className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-xs transition-colors ${chip}`}
        >
          <span className="min-w-0 flex-1">
            {l.groupLabel ? (
              <span className={`block text-[10px] font-medium uppercase tracking-wide ${muted}`}>{l.groupLabel}</span>
            ) : null}
            <span className={`font-semibold leading-snug ${showEnglish ? '' : 'font-devanagari'}`}>{l.label}</span>
          </span>
          <ChevronRight className={`h-3.5 w-3.5 shrink-0 ${muted}`} aria-hidden />
        </button>
      ))}
    </ExpandableSection>
  );
}
